/**
 * App shell — picks a screen based on global store state. Phase 3 agents fill
 * the three screens (RoomGate, Lobby, Table) against the fixed hook surface
 * in `state/hooks.ts` and the action surface in `wire/actions.ts`.
 *
 * Selection rules:
 *   - No room bound       → <RoomGate />        (pre-join)
 *   - phase === 'lobby'   → <Lobby />           (A3.3)
 *   - else                → <Table />           (A3.1/A3.2)
 * Errors are always rendered in <ErrorToasts /> (a global overlay).
 *
 * Phase-6 (v1.2) motion wrapping:
 *   - <MotionConfig reducedMotion="user"> — every motion component snaps
 *     instead of animating when the OS exposes prefers-reduced-motion.
 *   - <LazyMotion features={loadDomMaxFeatures} strict> — the dom-max
 *     features bundle (~29 kB gzip) lives in a separate chunk via the
 *     `./motion-features.js` indirection (see comment by the import).
 *     `strict` forbids the heavyweight `motion.*` components; consumers
 *     must use `m.*` from "motion/react".
 *   The LazyMotion + MotionConfig shell itself costs ~11 kB gzip on the
 *   initial bundle (measured 73.38 → 83.98 kB at Phase 6 ship). The 29 kB
 *   feature chunk loads on demand when the first `<m.*>` mounts (Phase 10).
 */
import { LazyMotion, MotionConfig } from "motion/react";

// Lazy-load the dom-max feature bundle so Phases 6-9 don't pay for runtime
// animation code that no component actually uses yet. The chunk lands the
// moment the first <m.*> mounts (Phase 10's card flight + pile sweep).
// The features live in a separate module (motion-features.ts) so Vite can
// actually code-split — a dynamic import of "motion/react" itself just
// resolves to the same chunk as the static LazyMotion/MotionConfig import
// above (Vite chunking pins barrel modules to a single chunk).
const loadDomMaxFeatures = () =>
  import("./motion-features.js").then((mod) => mod.default);
import { ConnectionBanner } from "./components/ConnectionBanner.js";
import { DisconnectedPanel } from "./components/DisconnectedPanel.js";
import { ErrorToasts } from "./components/ErrorToasts.js";
import { Lobby } from "./components/Lobby.js";
import { RoomGate } from "./components/RoomGate.js";
import { Table } from "./components/Table.js";
import { useConnection, useLobby, useRoomCode } from "./state/hooks.js";

export function App(): JSX.Element {
  const code = useRoomCode();
  const lobby = useLobby();
  const conn = useConnection();

  let screen: JSX.Element;
  // Closed-state short-circuit (Phase-3 UX-review F#7): when the socket is
  // terminally closed and we are bound to a room, replace whichever screen
  // would otherwise render with the global DisconnectedPanel. RoomGate stays
  // the right destination when there is no room yet (the socket has nothing
  // to be disconnected from).
  if (code && conn.kind === "closed") {
    screen = (
      <DisconnectedPanel
        reason={conn.reason}
        code={code}
        errorCode={conn.errorCode}
      />
    );
  } else if (!code) {
    screen = <RoomGate />;
  } else if (!lobby || lobby.phase === "lobby") {
    screen = <Lobby />;
  } else {
    screen = <Table />;
  }

  return (
    <MotionConfig reducedMotion="user">
      <LazyMotion features={loadDomMaxFeatures} strict>
        <div className="app-root">
          <ConnectionBanner />
          {screen}
          <ErrorToasts />
        </div>
      </LazyMotion>
    </MotionConfig>
  );
}
